import angular from 'angular';
import ngAnimate from 'angular-animate';
import ngTouch from 'angular-touch';
import ngCookies from 'angular-cookies';

import 'angular-min-max-validation';
import 'angular-i18n/angular-locale_ru.js';
import uiMask from 'angular-ui-mask';

import { getSearchParams, isErrorExcluded } from './utils';

const ui = [
  require('@cian/ui/app/components/arrow/index.jsm').default,
  require('../blocks/Button').Button,
  require('@cian/ui/app/components/checkbox-wrapper/index.jsm').default,
  require('@cian/ui/app/components/click-outside/index.jsm').default,
  require('@cian/ui/app/components/compile-directive/index.jsm').default,
  require('@cian/ui/app/components/countdown/index.jsm').default,
  require('@cian/ui/app/components/dropdown/index.jsm').default,
  require('@cian/ui/app/components/email-subdomain-validator/index.jsm').default,
  require('@cian/ui/app/components/input-wrapper/index.jsm').default,
  require('@cian/ui/app/components/modal/index.jsm').default,
  require('@cian/ui/app/components/ng-form-submit/index.jsm').default,
  require('@cian/ui/app/components/preloader/index.jsm').default,
  require('@cian/ui/app/components/prevent-submit-on-enter/index.jsm').default,
  require('@cian/ui/app/components/radio-wrapper/index.jsm').default,
  require('@cian/ui/app/components/ruble/index.jsm').default,
  require('@cian/ui/app/components/switcher/index.jsm').default,
  require('@cian/ui/app/components/tabs/index.jsm').default,
  require('@cian/ui/app/components/textarea/index.jsm').default,
  require('@cian/ui/app/components/tooltip/index.jsm').default,
  require('@cian/ui/app/components/typeahead/index.jsm').default,
  require('@cian/ui/app/utils/format-number/index.jsm').default,
  require('@cian/ui/app/utils/model-to-number/index.jsm').default,
  require('@cian/ui/app/utils/parse-share-amount/index.jsm').default,
  require('@cian/ui/app/utils/validate-integer/index.jsm').default,
  require('@cian/ui/app/utils/validate-not-zero/index.jsm').default,
];

import cianPublishTerms from '@cian/publish-terms/src';
import { calculateEnvironmentByHost } from '@cian/utils/lib/shared/calculate_environment_by_host';
import declareCianSentryConfiguration from '@cian/sentry';
import { getLogger } from '@cian/logger/browser';
import { getConfig } from '@cian/config/browser';

// modules
import authModule from './../blocks/auth/auth.module';
import socialModule from './../blocks/social/social.module';
import feedbackAddressMap from './../blocks/modals/modal-report-address/map';
import addressInput from './../blocks/modals/modal-report-address/address-input';

// adapters
import modalAdapter from './../adapters/ui.bootstrap.modal/modal.module';

// segments directives
import aboutObjectDirective from './../blocks/segments/about-object/about-object.directive';
import adPlacingTermsDirective from './../blocks/segments/ad-placing-terms/ad-placing-terms.directive';
import adTypeDirective from './../blocks/segments/ad-type/ad-type.directive';
import authDirective from './../blocks/segments/auth/auth.directive';
import authLastStepComponent from './../blocks/segments/auth-last-step/auth-last-step.component';
import infrastructureDirective from './../blocks/segments/infrastructure/infrastructure.directive';
import infrastructureAndLeisureDirective from './../blocks/segments/infrastructure-and-leisure/infrastructure-and-leisure.directive';
import priceAndDealTermsDirective from './../blocks/segments/price-and-deal-terms/price-and-deal-terms.directive';
import thisObjectOnOtherWebsitesDirective from './../blocks/segments/this-object-on-other-websites/this-object-on-other-websites.directive';
import shortFormSwitcherDirective from './../blocks/segments/short-form-switcher/short-form-switcher.directive';
import rentHelperDirective from './../blocks/segments/rent-helper/rent-helper.directive';
import disabledWarningDirective from './../blocks/segments/disabled-warning/disabled-warning.directive';
import homeownerWarningDirective from './../blocks/segments/homeowner-warning/homeowner-warning.directive';
import getAdditionalBenefitsDirective from './../blocks/segments/get-additional-benefits/get-additional-benefits.directive';
import indexingDelayAlert from './../blocks/segments/indexing-delay-alert/indexing-delay-alert.directive';
import coworkingWorkplaceWarning from './../blocks/segments/coworking-workplace-warning/coworking-workplace-warning.directive';
import coworkingWorkplaceBanner from './../blocks/segments/coworking-workplace-banner/coworking-workplace-banner.directive';
import { AuctionHint } from '../blocks/AuctionHint';
import amenitiesDirective from './../blocks/segments/amenities/amenities.directive';

// other directives
import adSegmentsDirective from './../blocks/ad-segments/ad-segments.directive';
import addFormDirective from './../blocks/add-form/add-form.directive';
import navigationDirective from './../blocks/navigation/navigation.directive';
import dynamicSegmentInputsDirective from './../blocks/dynamic-segment-inputs/dynamic-segment-inputs.directive';
import paymentStateDirective from './../blocks/payment-state/payment-state.directive';
import ngScopeElementDirective from './directives/ng-scope-element.directive';
import validatorNamesDirective from './directives/validator-names.directive';
import validatorInnDirective from './directives/validator-inn.directive';

import externalErrorList from './external-error-list';

// services
import adSegmentsService from './services/ad-segments.service';
import adStateService from './services/ad-state.service';
import adTooltipsService from './services/ad-tooltips.service';
import { addFormConfig } from './services/add-form.config';
import apiService from './services/api.service';
import apiRequestsUrlPrefixer from './services/api-requests-url-prefixer.interceptor';
import categoryService from './services/category.service';
import userService from './services/user.service';
import apiResponseErrorService from './services/http-response-error.interceptor';
import reportService from './services/report.service';
import adAssessmentService from './services/ad-assessment.service';
import headerPromoService from './services/header-promo.service';
import userIdentificationService from './services/user-identification.service';
import analyticsService from './services/analytics.service';
import homeownerService from './services/homeowner.service';
import commercialOwnerService from './services/commercialOwner.service';
import advertiseObjectService from './services/advertise-object.service';
import geoService from './services/geo.service';
import StatsService from './services/stats.service.provider';
import cardPaymentService from './services/card-payment.service';
import subdomainDraftService from './services/subdomain-draft.service';
import urlService from './services/url.service';
import ivrService from './services/ivr.service';
import { getAddFormMetaService } from './services/getAddFormMetaService';
import publishErrorTelemetryService from './services/publish-error-telemetry.service';
import deviceService from './services/device.service';
import excludeUtilitiesTermsRegionsService from './services/excludeUtilitiesTermsRegions.service';
import dealRentTrapService from './services/deal-rent-trap-after-fp.service';
import dealRentService from './services/rent-deal.service';
import { PlanService } from './services/plan.service';
import { AdFieldsCleaningService } from './services/ad-fields-cleaning.service';
import { TelemetryService } from './services/telemetry.service';
import coworkingService from './services/coworking.service';

import * as loggerWithCache from './logger-with-cache';

const matchUrl = () => true;
const release = process.env.SENTRY_RELEASE;
const environment = calculateEnvironmentByHost(window.location.host);
const dsn = '//9f902f82832644ca855e6f4ad0c47666@sentry-front.cian.ru/14';

declareCianSentryConfiguration({
  dsn: dsn,
  release: release,
  environment: environment,
  matchUrl: matchUrl
});

// adBlock у некоторых пользователей блокирует скрипт sentry.cian.site/sentry.min.js,
// поэтому в window может не быть Raven
if (window.Raven) {
  // require работает, потому что в node_modules есть raven-js от @cian/sentry
  // TODO надо решать это на уровне @cian/sentry

  window.Raven
    .setDataCallback((data) => {
      let logger = null;
      let config = null;

      try {
        logger = getLogger();
        config = getConfig();
      } catch (e) {}

      if (logger && config) {
        const isLogSendingEnabled = config.get('addformConfig.isLogSendingEnabled');

        if (!isLogSendingEnabled) {
          return data;
        }

        const { exception } = data;

        const meta = {};

        let message = 'Unknown error';

        if (
          exception &&
          exception.values &&
          Array.isArray(exception.values) &&
          exception.values.length > 0
        ) {
          const values = exception.values;
          message = `${values[0].type}: ${values[0].value}`;

          // Так как в chrome версии 49 сыпятся ошибки, которые сложно фиксать из-за конфликтов полифилов, а так же
          // данная версия хрома не относится к поддерживаемым, было решено завести список ошибок в runtime settings,
          // которые не будут логироваться.
          const excludedFromSendingErrors = config.get('addformConfig.excludedFromSendingErrors');

          const isExcludedFromTempList = excludedFromSendingErrors &&
            excludedFromSendingErrors.length &&
            isErrorExcluded(excludedFromSendingErrors, message);

          const isExlucdedFromPermanentList = isErrorExcluded(externalErrorList, message);

          if (isExcludedFromTempList || isExlucdedFromPermanentList) {
            return {};
          }

          const { stacktrace } = values[0];

          if (
            stacktrace &&
            Array.isArray(stacktrace.frames) &&
            stacktrace.frames.length > 0
          ) {
            meta.stacktrace = stacktrace.frames
              .map(
                frame => `\nat ${frame.function} (${frame.filename}:${frame.lineno}:${frame.colno})`
              )
              .join('; ');
          }
        }

        const cacheCountLimit = config.get('addformConfig.errorCacheCountLimit') || 10;
        const disableCache = config.get('addformConfig.disableErrorCache') || false;

        if (!disableCache) {
          loggerWithCache.error(logger, message, meta, { cacheCountLimit });
        } else {
          logger.error(message, meta);
        }
      }

      return data;
    })
    .addPlugin(require('raven-js/plugins/angular'), angular)
    .install();
}

const getAllModulesInDirectory = req => {
  return req.keys()
    .reduce((modules, path) => [...modules, req(path).default], [])
    .filter(m => m); // filter modules which export nothing
};

const segments = [
  // подключаем файлы, оканчивающиеся на js и ts, за исключением *.d.js и *.d.ts
  ...getAllModulesInDirectory(require.context('./segment-inputs-config/common', true, /((?<!\.d)(?<!\.test))\.(js|ts)$/im)),
  ...getAllModulesInDirectory(require.context('./segment-inputs-config/address', true, /((?<!\.d)(?<!\.test))\.(js|ts)$/im)),
  ...getAllModulesInDirectory(require.context('./segment-inputs-config/about-object', true, /((?<!\.d)(?<!\.test))\.(js|ts)$/im)),
  ...getAllModulesInDirectory(require.context('./segment-inputs-config/about-building', true, /((?<!\.d)(?<!\.test))\.(js|ts)$/im)),
  ...getAllModulesInDirectory(require.context('./segment-inputs-config/price-and-deal-terms', true, /((?<!\.d)(?<!\.test))\.(js|ts)$/im)),
  require('../blocks/icon-directive/icon-directive').default,
  require('../blocks/metro-icon/metro-icon.dir').default,
  require('../blocks/photo-upload/photo-upload.component').default,
  require('../blocks/mobile-click/mobile-click-directive').default,
  require('../blocks/dropdown/dropdown.directive').default,
  require('../blocks/plan-upload/plan-upload').default,
];

const dependencies = ui.concat([
  ngAnimate,
  ngTouch,
  ngCookies,
  cianPublishTerms,
  'ui.bootstrap.tooltip',
  'ui.bootstrap.stackedMap',
  'ui.bootstrap.modal',
  'minMaxValidations',
  uiMask,
  authModule,
  socialModule,
  feedbackAddressMap,
  addressInput,
  modalAdapter,
  ...segments,
]);

if (window.Raven) {
  dependencies.push('ngRaven');
}

export default angular.module('cian.addForm', dependencies)
  // segments directives
  .directive('cianAfAboutObject', aboutObjectDirective)
  .directive('cianAfAdPlacingTerms', adPlacingTermsDirective)
  .directive('cianAfAdType', adTypeDirective)
  .directive('cianAfAuth', authDirective)
  .directive('cianAfAmenities', amenitiesDirective)
  .component('cianAfAuthLastStep', authLastStepComponent)
  .directive('cianAfInfrastructure', infrastructureDirective)
  .directive('cianAfInfrastructureAndLeisure', infrastructureAndLeisureDirective)
  .directive('cianAfPriceAndDealTerms', priceAndDealTermsDirective)
  .directive('cianAfThisObjectOnOtherWebsites', thisObjectOnOtherWebsitesDirective)
  .directive('cianAfShortFormSwitcher', shortFormSwitcherDirective)
  .directive('cianAfRentHelper', rentHelperDirective)
  .directive('cianAfDisabledWarning', disabledWarningDirective)
  .directive('cianAfHomeownerWarning', homeownerWarningDirective)
  .directive('cianAfGetAdditionalBenefits', getAdditionalBenefitsDirective)
  .directive('cianAfIndexingDelayAlert', indexingDelayAlert)
  .directive('cianAfAboutObjectCoworkingWorkplaceWarning', coworkingWorkplaceWarning)
  .directive('cianAfCoworkingWorkplaceBanner', coworkingWorkplaceBanner)
  // other directives
  .directive('cianAfAdSegments', adSegmentsDirective)
  .directive('cianAfAddForm', addFormDirective)
  .directive('cianAfNavigation', navigationDirective)
  .directive('cianAfDynamicSegmentInputs', dynamicSegmentInputsDirective)
  .directive('cianAfPaymentState', paymentStateDirective)
  .directive('ngScopeElement', ngScopeElementDirective)
  .directive('cianAuctionHint', AuctionHint)
  // validators
  .directive('validatorNames', validatorNamesDirective)
  .directive('validatorInn', validatorInnDirective)
  // services
  .factory('adSegmentsService', adSegmentsService)
  .factory('adStateService', adStateService)
  .factory('adTooltipsService', adTooltipsService)
  .constant('addFormConfig', addFormConfig)
  .factory('apiService', apiService)
  .provider('categoryService', categoryService)
  .service('userService', userService)
  .service('geoService', geoService)
  .service('analyticsService', analyticsService)
  .factory('homeownerService', homeownerService)
  .factory('advertiseObjectService', advertiseObjectService)
  .provider('StatsService', StatsService)
  .factory('reportService', reportService)
  .factory('adAssessmentService', adAssessmentService)
  .factory('headerPromoService', headerPromoService)
  .service('userIdentificationService', userIdentificationService)
  .factory('cardPaymentService', cardPaymentService)
  .service('subdomainDraftService', subdomainDraftService)
  .service('urlService', urlService)
  .service('publishErrorTelemetryService', publishErrorTelemetryService)
  .factory('ivrService', ivrService)
  .service('getAddFormMetaService', getAddFormMetaService)
  .service('deviceService', deviceService)
  .service('excludeUtilitiesTermsRegionsService', excludeUtilitiesTermsRegionsService)
  .service('dealRentTrapService', dealRentTrapService)
  .service('dealRentService', dealRentService)
  .service('planService', PlanService)
  .service('adFieldsCleaningService', AdFieldsCleaningService)
  .service('telemetryService', TelemetryService)
  .service('coworkingService', coworkingService)
  .service('commercialOwnerService', commercialOwnerService)

  .config(['$provide', '$compileProvider', '$httpProvider', '$uibTooltipProvider', 'categoryServiceProvider', 'uiMask.ConfigProvider', ($provide, $compileProvider, $httpProvider, $uibTooltipProvider, categoryServiceProvider, uiMaskConfigProvider) => {
    $compileProvider.debugInfoEnabled(process.env.NODE_ENV === 'development');
    $provide.constant('isDebugMode', $compileProvider.debugInfoEnabled());
    $uibTooltipProvider.options({
      placement: window.serverAdaptiveFlags && window.serverAdaptiveFlags.isMobile ? 'top' : 'right',
      'popupDelay': 0,
    });
    $httpProvider.interceptors.push(apiRequestsUrlPrefixer);
    $httpProvider.interceptors.push(apiResponseErrorService);
    $httpProvider.interceptors.push('retryInterceptor');
    $httpProvider.interceptors.push('latestBackendResponseInterceptor');

    // disable cache for get requests in IE
    if (!$httpProvider.defaults.headers.get) {
      $httpProvider.defaults.headers.get = {};
    }
    $httpProvider.defaults.headers.get['Cache-Control'] = 'no-cache';
    $httpProvider.defaults.headers.get['Pragma'] = 'no-cache';

    categoryServiceProvider.setSearch(getSearchParams());
    uiMaskConfigProvider.clearOnBlur(false);
  }]).run([
    '$timeout', 'telemetryService',
    function ($timeout, telemetryService)
    {
      $timeout(function() {
        telemetryService.mark('addform.init-start');
      });
    }
  ]);
